import { useSearchParams } from "@solidjs/router";
import type { Issue } from "contracts/api/issue/responses";
import { createMemo, Show } from "solid-js";
import type { SummaryQuickLook } from "../../enums/scan";
import {
  QUICK_LOOK_SOURCE,
  SCAN_SECTION_KEY,
  SUMMARY_QUICK_LOOK,
} from "../../enums/scan";
import { KEYBOARD_SCOPE } from "../../enums/shell";
import { setScope } from "../../keyboard/manager";
import { singleParam } from "../../ui/searchParam";
import { isOpen } from "../website/issueModel";
import { createWebsiteStore } from "../website/store";
import { ContentFitnessProvenance } from "./ContentFitnessProvenance";
import { IssueQuickLookDrawer } from "./QuickLookDrawer";
import { ScanRouteHeader } from "./ScanRouteHeader";
import { ScanShell } from "./ScanShell";
import { EstatePreview } from "./SummaryEstatePreview";
import { SummaryQuickLookDrawer } from "./SummaryQuickLookDrawer";
import { SummaryScanScope } from "./SummaryScanScope";
import { overviewScanAvailability } from "./scanAvailability";
import { createCompletedRunForReport, scanCoverage } from "./scanCoverage";
import { contentFitnessHeadline } from "./scanModel";
import { Priorities } from "./summaryFindings";
import {
  executiveVerdict,
  Info,
  isSummaryQuickLook,
  scoreBand,
  scoreBandLabel,
  summaryScoreDelta,
  verdictBody,
} from "./summaryModel";
import {
  BrandGuidelinesPreview,
  ContentFitnessPreview,
} from "./summaryPillars";

export function ScanSummary(props: { websiteId: string }) {
  setScope(KEYBOARD_SCOPE.LIST);
  const context = createWebsiteStore(props.websiteId);
  const data = () => context.data();
  const completedRun = createCompletedRunForReport(props.websiteId, data);
  const headline = createMemo(() => {
    const value = data();
    return value ? contentFitnessHeadline(value) : null;
  });
  const [search, setSearch] = useSearchParams();
  const coverage = createMemo(() => {
    const value = data();
    return value ? scanCoverage(value, completedRun()) : null;
  });
  const combinedCoverage = createMemo(() => {
    const current = coverage();
    if (!current || current.completedPages === 0) return null;
    return current.completedPages !== null &&
      current.failedPages === 0 &&
      current.skippedPages === 0 &&
      current.inventoryPages === current.completedPages &&
      current.completedPages === current.assessedPages &&
      current.assessedPages === current.scoredPages
      ? current.completedPages
      : null;
  });
  const scanState = createMemo(() => {
    const value = data();
    return value ? overviewScanAvailability(value) : null;
  });
  const scanScope = createMemo(() => {
    const currentCoverage = coverage();
    const availability = scanState();
    return currentCoverage && availability
      ? { coverage: currentCoverage, availability }
      : null;
  });
  const selectedIssueId = createMemo(() => singleParam(search.issue));
  const selectedIssue = createMemo(
    () =>
      data()?.issues.find((issue) => issue.id === selectedIssueId()) ?? null,
  );
  const selectedQuickLook = createMemo(() => {
    const value = singleParam(search.quick);
    return isSummaryQuickLook(value) ? value : null;
  });
  const openQuickLook = (issue: Issue) => {
    setSearch({ quick: undefined, issue: issue.id });
  };
  const openSummaryQuickLook = (kind: SummaryQuickLook) => {
    setSearch({ issue: undefined, quick: kind });
  };
  const closeQuickLook = () => {
    setSearch({ issue: undefined, quick: undefined });
  };

  return (
    <ScanShell context={context} active={SCAN_SECTION_KEY.SUMMARY}>
      <Show
        when={data()}
        keyed
        fallback={<div class="loading signal-loading">Loading scan...</div>}
      >
        {(value) => (
          <div class="signal-page">
            <ScanRouteHeader
              websiteId={props.websiteId}
              section="Summary"
              title="Scan Summary"
              subtitle="Current health, priority findings and where to investigate next."
            />

            <section class="signal-hero">
              <div class="signal-hero-cell signal-score-cell">
                <span class="signal-card-label">
                  Content Fitness score
                  <Info text="Weighted across scored Content Fitness dimensions. Unscored dimensions are excluded and the remaining weights are renormalised." />
                </span>
                <div class="signal-big-score">
                  <strong>{headline()?.score ?? "--"}</strong>
                  <span>/100</span>
                </div>
                <span
                  class={`signal-score-status is-${scoreBand(headline()?.score ?? null)}`}
                >
                  {scoreBandLabel(headline()?.score ?? null)}
                </span>
                <Show when={headline()?.scoreDelta !== null}>
                  <small>
                    {summaryScoreDelta(headline()?.scoreDelta ?? 0)} since
                    previous scored run
                  </small>
                </Show>
                <Show when={headline()?.score === null}>
                  <small>
                    No Content Fitness dimensions were scored in this scan.
                  </small>
                </Show>
                <ContentFitnessProvenance
                  data={value}
                  completedRun={completedRun()}
                />
              </div>

              <div class="signal-hero-cell signal-verdict-cell">
                <span class="signal-card-label">
                  Executive verdict
                  <Info text="A concise interpretation of the current scan signals and open issue distribution." />
                </span>
                <h2>{executiveVerdict(value)}</h2>
                <p>{verdictBody(value, coverage())}</p>
                <div class="signal-inline-actions">
                  <button
                    type="button"
                    class="signal-text-action"
                    onClick={() =>
                      openSummaryQuickLook(SUMMARY_QUICK_LOOK.FITNESS)
                    }
                  >
                    Why this verdict
                  </button>
                  <a
                    class="signal-text-action"
                    href={`/w/${props.websiteId}/scan/issues`}
                  >
                    View top issues
                  </a>
                </div>
              </div>

              <Show when={scanScope()} keyed>
                {(scope) => (
                  <SummaryScanScope
                    coverage={scope.coverage}
                    combinedCoverage={combinedCoverage()}
                    openIssueCount={value.issues.filter(isOpen).length}
                    availability={scope.availability}
                    pagesHref={`/w/${props.websiteId}/scan/pages`}
                  />
                )}
              </Show>
            </section>

            <Priorities data={value} openQuickLook={openQuickLook} />

            <ContentFitnessPreview
              data={value}
              openQuickLook={openSummaryQuickLook}
            />

            <EstatePreview data={value} websiteId={props.websiteId} />

            <BrandGuidelinesPreview
              data={value}
              websiteId={props.websiteId}
              openQuickLook={openSummaryQuickLook}
            />

            <Show when={selectedIssue()} keyed>
              {(issue) => (
                <IssueQuickLookDrawer
                  issue={issue}
                  websiteId={props.websiteId}
                  data={value}
                  source={QUICK_LOOK_SOURCE.SUMMARY}
                  onClose={closeQuickLook}
                />
              )}
            </Show>
            <Show when={selectedQuickLook()} keyed>
              {(kind) => (
                <SummaryQuickLookDrawer
                  kind={kind}
                  websiteId={props.websiteId}
                  data={value}
                  onClose={closeQuickLook}
                />
              )}
            </Show>
          </div>
        )}
      </Show>
    </ScanShell>
  );
}
